import { computed } from 'vue';
import { driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useMapGetter } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

const openContactPanel = updateUISettings => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
    is_templates_panel_open: false,
  });
};

const openTemplatesPanel = updateUISettings => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_templates_panel_open: true,
  });
};

const closePanels = updateUISettings => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
    is_templates_panel_open: false,
  });
};

// Pasos cuyo elemento puede no existir en el DOM al inicio del tour porque
// dependen de que un panel esté abierto. Se excluyen del filtrado inicial.
const DEFERRED_ELEMENTS = new Set([
  '[data-zoho-tour="contact-info"]',
  '[data-zoho-tour="conversation-actions"]',
  '[data-zoho-tour="contact-notes"]',
  '[data-zoho-tour="conversation-participants"]',
  '[data-zoho-tour="templates-panel-content"]',
]);

const buildSteps = updateUISettings => [
  {
    element: '[data-zoho-tour="chat-list"]',
    popover: {
      title: '📋 Lista de conversaciones',
      description:
        'Aquí aparecen todas las conversaciones asignadas a ti. Puedes ver el estado, el contacto y el último mensaje. Haz clic en una conversación para abrirla.',
      side: 'right',
      align: 'start',
    },
    onHighlightStarted: () => closePanels(updateUISettings),
  },
  {
    element: '[data-zoho-tour="conversation-header"]',
    popover: {
      title: '📌 Cabecera de la conversación',
      description:
        'Muestra el nombre del contacto, el canal (WhatsApp, email…) y acciones rápidas como resolver, silenciar o asignar a otro agente.',
      side: 'bottom',
      align: 'start',
    },
  },
  {
    element: '[data-zoho-tour="reply-box"]',
    popover: {
      title: '✍️ Área de respuesta',
      description:
        'Escribe aquí tu mensaje antes de enviarlo. Puedes añadir emojis, archivos adjuntos y usar el editor de texto enriquecido.',
      side: 'top',
      align: 'start',
    },
  },
  {
    element: '[data-zoho-tour="editor-mode-toggle"]',
    popover: {
      title: '🔀 Modo de respuesta',
      description:
        '<strong>Responder</strong>: el cliente verá el mensaje.<br><strong>Nota privada</strong>: solo la ven los agentes del equipo, útil para coordinación interna.',
      side: 'top',
      align: 'start',
    },
  },
  {
    // Puede no existir si la conversación no es WhatsApp o la ventana está abierta.
    // En ese caso onHighlightStarted lo salta programáticamente.
    element: '[data-zoho-tour="whatsapp-template-btn"]',
    popover: {
      title: '📲 Plantillas de WhatsApp — ¡MUY IMPORTANTE!',
      description:
        'Si la conversación está cerrada o han pasado más de 24 h desde el último mensaje del cliente, <strong>solo puedes iniciar el contacto con una plantilla de WhatsApp</strong>. Haz clic aquí para seleccionar y enviar una plantilla aprobada.',
      side: 'top',
      align: 'end',
    },
    onHighlightStarted: (_el, _step, { driver: d }) => {
      if (!document.querySelector('[data-zoho-tour="whatsapp-template-btn"]')) {
        d.moveNext();
      }
    },
  },
  // --- Bloque: Panel de Contacto ---
  // onNextClick abre el panel al pulsar Siguiente desde el paso intro.
  // onHighlightStarted en contact-info garantiza que el panel esté abierto
  // incluso si el botón contact-panel-btn no estaba en el DOM (popover centrado).
  {
    element: '[data-zoho-tour="contact-panel-btn"]',
    popover: {
      title: '👤 Panel de Contacto',
      description:
        'Este botón abre el panel lateral de <strong>Contacto</strong>. Aquí encontrarás la información del cliente, las acciones de la conversación, notas y participantes. Vamos a verlos uno a uno.',
      side: 'left',
      align: 'start',
    },
    onNextClick: (_el, _step, { driver: d }) => {
      openContactPanel(updateUISettings);
      setTimeout(() => d.moveNext(), 300);
    },
  },
  {
    element: '[data-zoho-tour="contact-info"]',
    popover: {
      title: '🪪 Datos del contacto',
      description:
        'Nombre, teléfono, email y otros datos del cliente. Puedes editarlos haciendo clic en el icono de lápiz.',
      side: 'left',
      align: 'start',
    },
    onHighlightStarted: (_el, _step, { driver: d }) => {
      openContactPanel(updateUISettings);
      setTimeout(() => d.refresh(), 300);
    },
  },
  {
    element: '[data-zoho-tour="conversation-actions"]',
    popover: {
      title: '⚙️ Acciones de la conversación',
      description:
        'Asigna un <strong>agente responsable</strong>, un <strong>equipo</strong> y la <strong>prioridad</strong> de la conversación. También puedes añadir <strong>etiquetas</strong> para clasificarla.',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-zoho-tour="contact-notes"]',
    popover: {
      title: '📝 Notas de contacto',
      description:
        'Deja notas internas sobre el cliente, visibles en todas sus conversaciones. <em>Próximamente estas notas se sincronizarán con Zoho CRM.</em>',
      side: 'left',
      align: 'start',
    },
  },
  {
    element: '[data-zoho-tour="conversation-participants"]',
    popover: {
      title: '👥 Participantes de la conversación',
      description:
        'Añade a otros agentes para que puedan seguir y participar en esta conversación sin ser el responsable principal.',
      side: 'left',
      align: 'start',
    },
  },
  // --- Bloque: Panel de Plantillas ---
  {
    element: '[data-zoho-tour="templates-panel-btn"]',
    popover: {
      title: '💬 Panel de plantillas de texto',
      description:
        'Este botón abre el panel de <strong>plantillas de texto</strong> predefinidas del equipo. Vamos a ver su contenido.',
      side: 'left',
      align: 'start',
    },
    onNextClick: (_el, _step, { driver: d }) => {
      openTemplatesPanel(updateUISettings);
      setTimeout(() => d.moveNext(), 300);
    },
  },
  {
    element: '[data-zoho-tour="templates-panel-content"]',
    onHighlightStarted: (_el, _step, { driver: d }) => {
      openTemplatesPanel(updateUISettings);
      setTimeout(() => d.refresh(), 300);
    },
    popover: {
      title: '💬 Pestañas de plantillas',
      description:
        'Encontrarás las plantillas organizadas en 4 pestañas:<br>' +
        '• <strong>Comercial ES</strong> — plantillas de venta en español<br>' +
        '• <strong>Sales EN</strong> — sales templates in English<br>' +
        '• <strong>Marketing</strong> — plantillas de marketing<br>' +
        '• <strong>Utilidad</strong> — plantillas de servicio y soporte<br><br>' +
        'Al seleccionar una plantilla con variables <code>{{1}}</code>, <strong>deberás rellenar los campos</strong> con tu nombre u otro dato antes de enviarla.',
      side: 'left',
      align: 'start',
    },
  },
  {
    popover: {
      title: '🎉 ¡Ya estás listo!',
      description:
        'Ya conoces las herramientas principales. Recuerda:<br>' +
        '• Si el cliente no ha respondido en 24 h → usa una <strong>plantilla de WhatsApp</strong>.<br>' +
        '• Para mensajes predefinidos del equipo → usa el <strong>panel de plantillas</strong> (botón de burbuja en el lateral).<br>' +
        '• Al usar plantillas con variables, <strong>rellena siempre los campos</strong> con tu nombre o el dato que corresponda. ¡Buena suerte!',
    },
  },
];

export function useZohoTour() {
  const currentUser = useMapGetter('getCurrentUser');
  const { uiSettings, updateUISettings } = useUISettings();

  const shouldShowTour = computed(() => {
    if (!currentUser.value?.zoho_tour_enabled) return false;
    if (!currentUser.value?.zoho_source) return false;
    if (uiSettings.value?.zoho_tour_completed) return false;
    return true;
  });

  const markTourCompleted = () => {
    updateUISettings({ zoho_tour_completed: true });
  };

  const startTour = () => {
    const steps = buildSteps(updateUISettings);
    const filteredSteps = steps.filter(step => {
      if (!step.element) return true;
      if (DEFERRED_ELEMENTS.has(step.element)) return true;
      return !!document.querySelector(step.element);
    });
    const totalSteps = filteredSteps.length;
    // Track whether the user reached the last step and clicked "¡Listo!"
    let completedNaturally = false;

    const driverInstance = driver({
      showProgress: true,
      animate: true,
      overlayColor: 'rgba(0, 0, 0, 0.65)',
      stagePadding: 6,
      stageRadius: 8,
      allowClose: true,
      progressText: '{{current}} / {{total}}',
      nextBtnText: 'Siguiente →',
      prevBtnText: '← Anterior',
      doneBtnText: '¡Listo!',
      onDestroyStarted: () => {
        const activeIndex = driverInstance.getActiveIndex() ?? 0;
        // Last step has no element — reaching it means the user got to the end.
        // "¡Listo!" on the last step triggers destroy from index totalSteps - 1.
        const isLastStep = activeIndex >= totalSteps - 1;
        completedNaturally = isLastStep;
        updateUISettings({
          zoho_tour_completed: true,
          zoho_tour_skipped: !completedNaturally,
          zoho_tour_skipped_at_step: completedNaturally
            ? null
            : activeIndex + 1,
        });
        driverInstance.destroy();
      },
      // Solo filtramos elementos que definitivamente no existen en este contexto,
      // no los que dependen de que un panel esté abierto (DEFERRED_ELEMENTS).
      steps: filteredSteps,
    });

    driverInstance.drive();
  };

  return { shouldShowTour, startTour, markTourCompleted };
}
